import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Collapse,
    useMediaQuery,
    useTheme,
    Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { apiHandler } from '../../../../utils/api/responseHandler';
import config from '../../../../config';
import logger from '../../../../utils/logging/logger';
import { useComponentVersion } from '../../../../hooks/quoter/useComponentVersion';
import type { QuoteData, ClothData } from '../../../../types/quoter/types';

const componentName = 'ConfiguredFeaturesSummary';

// Styled component for collapsible section header (matches SailOptionsContainer style)
const CollapsibleHeaderStyled = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    padding: theme.spacing(1.5, 2),
    backgroundColor: `${theme.palette.primary.main}08`,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: '1px solid',
    borderColor: `${theme.palette.primary.main}30`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    transition: 'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
    '&:hover': {
        backgroundColor: `${theme.palette.primary.main}12`,
        borderColor: `${theme.palette.primary.main}50`,
        boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
    },
}));

interface ConfiguredFeaturesSummaryProps {
    quoteId: number;
    sailId: number;
    /** If true, shows compact badge on mobile instead of full list */
    compactOnMobile?: boolean;
}

interface FeatureSummaryState {
    quoteData: QuoteData | null;
    clothData: ClothData | null;
    isLoading: boolean;
    error: string | null;
}

const ConfiguredFeaturesSummary: React.FC<ConfiguredFeaturesSummaryProps> = ({
    quoteId,
    sailId,
    compactOnMobile = true
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isExpanded, setIsExpanded] = useState(false);

    const [state, setState] = useState<FeatureSummaryState>({
        quoteData: null,
        clothData: null,
        isLoading: true,
        error: null
    });

    const fetchFeatureData = useCallback(async () => {
        if (!quoteId || quoteId <= 0) return;

        setState(prev => ({ ...prev, isLoading: true, error: null }));

        try {
            logger.log('LogComponentUpdates', componentName, 'Fetching feature data for quote:', quoteId);

            // Fetch quote data (includes SelectionSummary from f_QuoteSelectionSummary)
            const quoteResponse = await apiHandler.fetch<QuoteData>(
                `${config.API_URL}/api/quotes/${quoteId}`
            );

            if (quoteResponse.error) throw new Error('Failed to fetch quote data');
            const quote = quoteResponse.data!;

            // Fetch cloth data
            const clothResponse = await apiHandler.fetch<ClothData>(
                `${config.API_URL}/api/quotes/${quoteId}/cloth-default`
            );

            setState({
                quoteData: quote,
                clothData: clothResponse.data || null,
                isLoading: false,
                error: null
            });

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to load features';
            logger.log('LogError', componentName, 'Error fetching feature data:', errorMessage);
            setState(prev => ({
                ...prev,
                error: errorMessage,
                isLoading: false
            }));
        }
    }, [quoteId]);

    // Subscribe to version changes to refresh when options change
    useComponentVersion('sailOptions', {
        onVersionChange: async () => {
            await fetchFeatureData();
        }
    });

    // Initial load
    useEffect(() => {
        fetchFeatureData();
    }, [fetchFeatureData]);

    const getClothDescription = (): string | null => {
        if (!state.clothData) return null;
        return `${state.clothData.Manufacturer} - ${state.clothData.ClothMakeName} ${state.clothData.WeightOz?.toLocaleString()}oz`;
    };

    // Parse SelectionSummary from database (pipe-delimited, ^ prefix for defaults)
    const getFeatures = (): { text: string; isDefault: boolean }[] => {
        if (!state.quoteData?.SelectionSummary) return [];
        return state.quoteData.SelectionSummary
            .split('|')
            .map(s => s.trim())
            .filter(s => s.length > 0)
            .map(s => ({
                text: s.startsWith('^') ? s.substring(1) : s,
                isDefault: s.startsWith('^')
            }));
    };

    if (state.isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" p={2}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (state.error) {
        return null; // Silently fail - not critical
    }

    const clothDescription = getClothDescription();
    const features = getFeatures();

    // Mobile compact view - collapsible
    if (isMobile && compactOnMobile) {
        return (
            <Box sx={{ mt: 2 }}>
                <CollapsibleHeaderStyled onClick={() => setIsExpanded(!isExpanded)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleOutlineIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight="medium">
                            Your Configuration
                        </Typography>
                        {features.length > 0 && (
                            <Chip
                                label={`${features.length} options`}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.75rem' }}
                            />
                        )}
                    </Box>
                    <ExpandMoreIcon
                        sx={{
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                        }}
                    />
                </CollapsibleHeaderStyled>
                <Collapse in={isExpanded}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        {clothDescription && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Cloth:</strong> {clothDescription}
                            </Typography>
                        )}
                        {features.length > 0 ? (
                            features.map((feature, index) => (
                                <Typography key={index} variant="body2" color="text.secondary">
                                    {feature.isDefault ? '✓' : '•'} {feature.text}
                                </Typography>
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Standard configuration selected
                            </Typography>
                        )}
                    </Box>
                </Collapse>
            </Box>
        );
    }

    // Desktop view - always show features
    return (
        <Box sx={{ mt: 2 }}>
            {clothDescription && (
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.25 }}>
                        Cloth
                    </Typography>
                    <Typography variant="body2">
                        {clothDescription}
                    </Typography>
                </Box>
            )}

            {features.length > 0 && (
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                        Options
                    </Typography>
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(2, 1fr)', 
                        gap: 0.5,
                        columnGap: 2
                    }}>
                        {features.map((feature, index) => (
                            <Typography key={index} variant="body2" sx={{ lineHeight: 1.6 }}>
                                {feature.isDefault ? '✓' : '•'} {feature.text}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            )}

            {features.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Standard configuration
                </Typography>
            )}
        </Box>
    );
};

export default ConfiguredFeaturesSummary;
